import { useId, useState } from "react";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Подсказка «что это за число и как его читать».
 *
 * Нативный `title` не годился: всплывает через полторы секунды, рвётся на
 * длинном тексте и недоступен с клавиатуры. Своя реализация — без новых
 * зависимостей: показывается по hover и по фокусу, закрывается по Esc.
 *
 * `as="card"` — вариант, где триггером служит вся карточка (наводить на
 * иконку размером с ноготь неудобно), а иконка остаётся визуальным признаком
 * «здесь есть объяснение».
 */
interface InfoTipProps {
    /** Текст подсказки. Первая фраза отвечает «что это», дальше — «как читать». */
    text: string;
    /** Куда раскрывать: вниз (по умолчанию) или вверх — для нижних строк блока. */
    side?: "top" | "bottom";
    /** Выравнивание по горизонтали относительно триггера. */
    align?: "start" | "center" | "end";
    /**
     * Позиционировать подсказку относительно родителя, а не самой метки. Нужно в
     * карточках: иначе popover раскрывается прямо под подписью и накрывает число,
     * ради которого его и открыли. Родитель обязан быть `relative`.
     */
    anchorToParent?: boolean;
    className?: string;
    children?: React.ReactNode;
}

export function InfoTip({ text, side = "bottom", align = "start", anchorToParent, className, children }: InfoTipProps) {
    const [open, setOpen] = useState(false);
    const id = useId();

    return (
        <span
            className={cn("inline-flex items-center gap-1 group", !anchorToParent && "relative", className)}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            {children}
            <button
                type="button"
                aria-label="Пояснение"
                aria-describedby={open ? id : undefined}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onClick={() => setOpen(v => !v)}
                onKeyDown={e => { if (e.key === "Escape") setOpen(false); }}
                className="text-muted-foreground/60 hover:text-foreground focus-visible:text-foreground transition-colors
                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
            >
                <Info className="h-3.5 w-3.5" />
            </button>
            {open && (
                <span
                    id={id}
                    role="tooltip"
                    className={cn(
                        "absolute z-30 w-72 max-w-[min(18rem,80vw)] rounded-lg border bg-popover px-3 py-2",
                        "text-xs font-normal leading-relaxed text-popover-foreground shadow-lg pointer-events-none",
                        side === "bottom" ? "top-full mt-1.5" : "bottom-full mb-1.5",
                        align === "start" ? "left-0" : align === "end" ? "right-0" : "left-1/2 -translate-x-1/2",
                    )}
                >
                    {text}
                </span>
            )}
        </span>
    );
}
